import React, { useCallback } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  SubCondition,
  ConditionQuantifier,
  HUB_MAX_SUB_CONDITIONS,
} from 'dorian-shared/types/branch/HubOption';
import type { TextDestCondition } from 'dorian-shared/types/branch/HubOption';
import { MemoryDTO } from 'components/pages/Book/MemoryBank/memoryBankTypes';
import { DragHandle } from 'components/ui/DragHandle/DragHandle';
import { createEmptySubCondition } from '../../stateConditionDefaults';
import { SubConditionRow } from '../SubConditionRow';
import { BranchSelectItem } from '../../shared/types';
import { TextDestResultRow } from './TextDestResultRow';
import './TextDestConditionCard.scss';

interface TextDestConditionCardProps {
  condition: TextDestCondition;
  memoryVariables: MemoryDTO[];
  branches: BranchSelectItem[];
  currentBranchId: number | null;
  isDisabled: boolean;
  isContinue: boolean;
  isValid: boolean;
  onChange: (updated: TextDestCondition) => void;
  onRemove: () => void;
}

export function TextDestConditionCard({
  condition,
  memoryVariables,
  branches,
  currentBranchId,
  isDisabled,
  isContinue,
  isValid,
  onChange,
  onRemove,
}: TextDestConditionCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: condition.uuid,
    disabled: isDisabled,
    transition: {
      duration: 250,
      easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleQuantifierChange = useCallback(
    (q: ConditionQuantifier) => {
      onChange({ ...condition, quantifier: q });
    },
    [condition, onChange],
  );

  const handleSubConditionChange = useCallback(
    (index: number, updated: SubCondition) => {
      const next = [...condition.subConditions];
      next[index] = updated;
      onChange({ ...condition, subConditions: next });
    },
    [condition, onChange],
  );

  const handleSubConditionRemove = useCallback(
    (index: number) => {
      if (condition.subConditions.length <= 1) return;
      const next = condition.subConditions.filter((_, i) => i !== index);
      onChange({ ...condition, subConditions: next });
    },
    [condition, onChange],
  );

  const handleAddSubCondition = useCallback(() => {
    if (condition.subConditions.length >= HUB_MAX_SUB_CONDITIONS) return;
    onChange({
      ...condition,
      subConditions: [...condition.subConditions, createEmptySubCondition()],
    });
  }, [condition, onChange]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`text-dest-condition-card p-2${!isValid ? ' text-dest-condition-card--error' : ''}`}
      data-testid="text-dest-condition-card"
    >
      <div className="d-flex align-items-start">
        <DragHandle
          attributes={attributes}
          listeners={listeners}
          disabled={isDisabled}
          className="mr-1"
        />

        <div className="flex-grow-1">
          {/* Quantifier toggle + sub-conditions */}
          <div className="text-dest-condition-card__conditions">
            {condition.subConditions.length > 1 && (
              <div className="d-flex align-items-center mb-2">
                <label className="mb-0 small mr-1" style={{ fontWeight: 600 }}>When</label>
                <div className="btn-group btn-group-sm">
                  <button
                    type="button"
                    className={`btn btn-sm ${condition.quantifier === ConditionQuantifier.All ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => handleQuantifierChange(ConditionQuantifier.All)}
                    disabled={isDisabled}
                  >
                    ALL
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${condition.quantifier === ConditionQuantifier.Any ? 'btn-primary' : 'btn-outline-secondary'}`}
                    onClick={() => handleQuantifierChange(ConditionQuantifier.Any)}
                    disabled={isDisabled}
                  >
                    ANY
                  </button>
                </div>
                <label className="mb-0 small ml-1">of these are true:</label>
              </div>
            )}

            {condition.subConditions.map((sub, idx) => (
              <SubConditionRow
                key={sub.uuid}
                subCondition={sub}
                memoryVariables={memoryVariables}
                isDisabled={isDisabled}
                canRemove={condition.subConditions.length > 1}
                onChange={(updated) => handleSubConditionChange(idx, updated)}
                onRemove={() => handleSubConditionRemove(idx)}
              />
            ))}

            {condition.subConditions.length < HUB_MAX_SUB_CONDITIONS && (
              <button
                type="button"
                className="btn btn-outline-info btn-sm mt-2"
                style={{ fontSize: '0.7rem' }}
                onClick={handleAddSubCondition}
                disabled={isDisabled}
              >
                + Sub-condition
              </button>
            )}
          </div>

          {/* Result row */}
          <TextDestResultRow
            condition={condition}
            branches={branches}
            currentBranchId={currentBranchId}
            isDisabled={isDisabled}
            isContinue={isContinue}
            onChange={onChange}
          />
        </div>

        {/* Remove button */}
        <button
          type="button"
          className="btn btn-outline-danger btn-sm ml-2"
          onClick={onRemove}
          disabled={isDisabled}
          title="Remove condition"
        >
          ×
        </button>
      </div>
    </div>
  );
}
